import { useEffect, useState } from "react";
import { supabase } from "@vinoga/db";
import { Undo2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useT } from "@/lib/i18n";

// "Atteikumi" — online contract-withdrawal submissions from the storefront
// returns page (Edgars doc 26.09.2026). Staff mark a row processed once the
// return/refund is handled.
type Row = {
  id: string;
  full_name: string;
  order_number: string;
  email: string;
  products: string;
  reason: string | null;
  status: string;
  created_at: string;
  processed_at: string | null;
};

const PAGE = 50;

export function WithdrawalRequests() {
  const t = useT();
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      let q = supabase
        .from("withdrawal_requests")
        .select("*", { count: "exact" })
        .order("created_at", { ascending: false })
        .range(page * PAGE, page * PAGE + PAGE - 1);
      if (search.trim()) {
        const s = search.trim();
        q = q.or(`email.ilike.%${s}%,order_number.ilike.%${s}%,full_name.ilike.%${s}%`);
      }
      const { data, count } = await q;
      if (cancelled) return;
      setRows((data ?? []) as Row[]);
      setTotal(count ?? 0);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [page, search, reload]);

  async function toggleProcessed(row: Row) {
    const processed = row.status !== "processed";
    await supabase
      .from("withdrawal_requests")
      .update({
        status: processed ? "processed" : "new",
        processed_at: processed ? new Date().toISOString() : null,
      })
      .eq("id", row.id);
    setReload((n) => n + 1);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-xl font-bold text-foreground">
            <Undo2 size={20} className="text-muted-foreground" />
            {t("withdrawals.title", "Atteikumi")}
          </h1>
          <p className="font-body text-sm text-muted-foreground">
            {t("withdrawals.subtitle", "Atteikuma pieteikumi no mājaslapas formas.")}{" "}
            {total} {t("withdrawals.total", "kopā")}
          </p>
        </div>
        <input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          placeholder={t("withdrawals.searchPlaceholder", "Meklēt pēc e-pasta, pasūtījuma, vārda…")}
          className="w-72 rounded-lg border border-border bg-card px-3 py-2 font-body text-sm text-foreground outline-none focus:border-primary"
        />
      </div>

      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("withdrawals.colDate", "Saņemts")}</TableHead>
              <TableHead>{t("withdrawals.colOrder", "Pasūtījums")}</TableHead>
              <TableHead>{t("withdrawals.colName", "Klients")}</TableHead>
              <TableHead>{t("withdrawals.colProducts", "Preces")}</TableHead>
              <TableHead>{t("withdrawals.colReason", "Iemesls")}</TableHead>
              <TableHead>{t("withdrawals.colStatus", "Statuss")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="whitespace-nowrap font-body text-sm text-muted-foreground">
                  {new Date(r.created_at).toLocaleDateString("lv-LV")}
                </TableCell>
                <TableCell className="whitespace-nowrap font-body text-sm font-medium">
                  {r.order_number}
                </TableCell>
                <TableCell className="font-body text-sm">
                  <div className="font-medium">{r.full_name}</div>
                  <div className="text-muted-foreground">{r.email}</div>
                </TableCell>
                <TableCell className="max-w-[280px] font-body text-sm text-muted-foreground">
                  <span className="line-clamp-3 whitespace-pre-wrap">{r.products}</span>
                </TableCell>
                <TableCell className="max-w-[220px] font-body text-sm text-muted-foreground">
                  <span className="line-clamp-3 whitespace-pre-wrap">{r.reason ?? "—"}</span>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <button
                    onClick={() => toggleProcessed(r)}
                    className={
                      r.status === "processed"
                        ? "rounded bg-emerald-500/15 px-2 py-0.5 font-body text-xs font-medium text-emerald-700"
                        : "rounded bg-amber-500/15 px-2 py-0.5 font-body text-xs font-medium text-amber-700"
                    }
                    title={t("withdrawals.toggleHint", "Klikšķini, lai mainītu statusu")}
                  >
                    {r.status === "processed"
                      ? t("withdrawals.processed", "apstrādāts")
                      : t("withdrawals.new", "jauns")}
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center font-body text-sm text-muted-foreground">
                  {t("withdrawals.empty", "Atteikumu vēl nav.")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {total > PAGE && (
        <div className="flex items-center justify-center gap-3 font-body text-sm">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            className="rounded border border-border px-3 py-1.5 disabled:opacity-40"
          >
            ←
          </button>
          <span className="text-muted-foreground">
            {page + 1} / {Math.ceil(total / PAGE)}
          </span>
          <button
            disabled={(page + 1) * PAGE >= total}
            onClick={() => setPage((p) => p + 1)}
            className="rounded border border-border px-3 py-1.5 disabled:opacity-40"
          >
            →
          </button>
        </div>
      )}
    </div>
  );
}
